import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useShowBanner } from '../hooks/useShowBanner';

const NewDashboardAnnouncementModal = () => {
  const { t } = useTranslation();
  const { shouldShowBanner } = useShowBanner();
  const handleGoToNewDashboard = () => {
    window.open('https://app.memowrite.com', '_blank');
  };

  const features = [
    {
      icon: '🚀',
      title: t('newDashboardAnnouncement.writeRefineTitle'),
      description: t('newDashboardAnnouncement.writeRefineDescription'),
    },
    {
      icon: '✍️',
      title: t('newDashboardAnnouncement.controlTitle'),
      description: t('newDashboardAnnouncement.controlDescription'),
    },
    {
      icon: '📦',
      title: t('newDashboardAnnouncement.fasterDeliveryTitle'),
      description: t('newDashboardAnnouncement.fasterDeliveryDescription'),
    },
    {
      icon: '🎯',
      title: t('newDashboardAnnouncement.writingFlowTitle'),
      description: t('newDashboardAnnouncement.writingFlowDescription'),
    },
  ];

  return (
    <Modal
      show={shouldShowBanner}
      onHide={() => {}}
      centered
      dialogClassName="new-dashboard-modal"
    >
      <style>{`
        .new-dashboard-modal .modal-content {
          border-radius: 16px;
          overflow: hidden;
          border: none;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }
        .new-dashboard-modal .modal-dialog {
          max-width: 480px;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes pulse-ring {
          0% { transform: scale(0.95); opacity: 1; }
          50% { transform: scale(1); opacity: 0.8; }
          100% { transform: scale(0.95); opacity: 1; }
        }
      `}</style>

      <div
        style={{
          background:
            'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
          padding: '32px 28px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative elements */}
        <div
          style={{
            position: 'absolute',
            top: -60,
            right: -60,
            width: 180,
            height: 180,
            background:
              'radial-gradient(circle, rgba(255, 195, 113, 0.15) 0%, transparent 70%)',
            borderRadius: '50%',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: -40,
            left: -40,
            width: 120,
            height: 120,
            background:
              'radial-gradient(circle, rgba(255, 195, 113, 0.1) 0%, transparent 70%)',
            borderRadius: '50%',
          }}
        />

        {/* Sparkle icon */}
        <div
          style={{
            width: 56,
            height: 56,
            margin: '0 auto 20px',
            background: 'linear-gradient(135deg, #ffc371 0%, #ff9a56 100%)',
            borderRadius: 14,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'float 3s ease-in-out infinite',
            boxShadow: '0 8px 24px rgba(255, 195, 113, 0.3)',
          }}
        >
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#1a1a2e"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        </div>

        {/* Badge */}
        <div
          style={{
            display: 'inline-block',
            background: 'linear-gradient(90deg, #ffc371, #ff9a56, #ffc371)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 2s linear infinite',
            color: '#1a1a2e',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 1.5,
            padding: '5px 12px',
            borderRadius: 20,
            marginBottom: 14,
            textTransform: 'uppercase',
          }}
        >
          ✨ {t('newDashboardAnnouncement.badge')}
        </div>

        <h2
          style={{
            fontFamily: "'EB Garamond', serif",
            color: '#ffffff',
            fontSize: 24,
            fontWeight: 600,
            marginBottom: 10,
            lineHeight: 1.3,
          }}
        >
          {t('newDashboardAnnouncement.title')}
        </h2>

        <p
          style={{
            color: 'rgba(255, 255, 255, 0.75)',
            fontSize: 14,
            lineHeight: 1.5,
            marginBottom: 0,
          }}
        >
          {t('newDashboardAnnouncement.description')}
        </p>
      </div>

      <Modal.Body style={{ padding: '24px 28px', backgroundColor: '#fefefe' }}>
        {/* Features section */}
        <p
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: '#1a1a2e',
            marginBottom: 16,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          {t('newDashboardAnnouncement.featuresTitle')}
        </p>

        <div style={{ marginBottom: 20 }}>
          {features.map((feature, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '10px 0',
                borderBottom:
                  index < features.length - 1
                    ? '1px solid rgba(0, 0, 0, 0.06)'
                    : 'none',
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  backgroundColor: '#fff3cc',
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 16,
                  flexShrink: 0,
                }}
              >
                {feature.icon}
              </div>
              <div>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 14,
                    color: '#1a1a2e',
                    marginBottom: 2,
                  }}
                >
                  {feature.title}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: '#666',
                    lineHeight: 1.4,
                  }}
                >
                  {feature.description}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* How to access section */}
        <div
          style={{
            backgroundColor: '#f8f9fa',
            borderRadius: 10,
            padding: '14px 16px',
            marginBottom: 16,
          }}
        >
          <p
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: '#1a1a2e',
              marginBottom: 8,
            }}
          >
            {t('newDashboardAnnouncement.howToAccessTitle')}
          </p>
          <p
            style={{
              fontSize: 12,
              color: '#555',
              lineHeight: 1.5,
              marginBottom: 0,
            }}
          >
            {t('newDashboardAnnouncement.howToAccessDescription')}{' '}
            <strong style={{ color: '#1a1a2e' }}>app.memowrite.com</strong>.{' '}
            {t('newDashboardAnnouncement.howToAccessMagicLink')}
          </p>
        </div>

        {/* Security note */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 20,
            padding: '10px 14px',
            backgroundColor: '#e8f5e9',
            borderRadius: 8,
          }}
        >
          <span style={{ fontSize: 16 }}>🔒</span>
          <span style={{ fontSize: 12, color: '#2e7d32', fontWeight: 500 }}>
            {t('newDashboardAnnouncement.securityNote')}
          </span>
        </div>

        <button
          onClick={handleGoToNewDashboard}
          style={{
            width: '100%',
            padding: '14px 20px',
            fontSize: 15,
            fontWeight: 600,
            color: '#1a1a2e',
            background: 'linear-gradient(135deg, #ffc371 0%, #ffb347 100%)',
            border: 'none',
            borderRadius: 10,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 14px rgba(255, 195, 113, 0.4)',
            animation: 'pulse-ring 2s ease-in-out infinite',
          }}
          onMouseOver={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 6px 20px rgba(255, 195, 113, 0.5)';
          }}
          onMouseOut={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 14px rgba(255, 195, 113, 0.4)';
          }}
        >
          👉 {t('newDashboardAnnouncement.continueButton')}
        </button>
      </Modal.Body>
    </Modal>
  );
};

export default NewDashboardAnnouncementModal;
